import { analyticsManager, PropertyEventParams } from '../analytics';

/**
 * Track property view event
 */
export const trackViewProperty = (propertyId: string, parentAccountId?: string, category?: string) => {
  analyticsManager.trackPropertyEvent('view', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    category: category || 'property',
  });
};

/**
 * Track property creation start
 */
export const trackCreatePropertyStart = (parentAccountId?: string) => {
  analyticsManager.trackPropertyEvent('create', {
    property_id: 'new',
    parent_account_id: parentAccountId,
    action: 'start',
    status: 'initiated',
  });
};

/**
 * Track property creation completion
 */
export const trackCreatePropertyComplete = (propertyId: string, parentAccountId?: string, category?: string) => {
  analyticsManager.trackPropertyEvent('create', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    category: category || 'property',
    action: 'complete',
    status: 'success',
  });
};

/**
 * Track property update event
 */
export const trackUpdateProperty = (propertyId: string, parentAccountId?: string, fieldsUpdated?: string[], status?: string) => {
  analyticsManager.trackPropertyEvent('update', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    action: 'update',
    status: status || 'success',
  });

  // Track individual field updates as custom event for detailed analysis
  if (fieldsUpdated && fieldsUpdated.length > 0) {
    analyticsManager.trackCustomEvent('property_fields_updated', {
      property_id: propertyId,
      parent_account_id: parentAccountId,
      fields_count: fieldsUpdated.length,
      fields: fieldsUpdated.join(','),
    });
  }
};

/**
 * Track property deletion
 */
export const trackDeleteProperty = (propertyId: string, parentAccountId?: string, reason?: string) => {
  analyticsManager.trackPropertyEvent('delete', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    action: 'delete',
    status: 'success',
  });

  // Track deletion reason for insights
  if (reason) {
    analyticsManager.trackCustomEvent('property_deleted', {
      property_id: propertyId,
      parent_account_id: parentAccountId,
      deletion_reason: reason,
    });
  }
};

/**
 * Track property publish event
 */
export const trackPublishProperty = (propertyId: string, parentAccountId?: string, publishType?: 'live' | 'draft') => {
  analyticsManager.trackPropertyEvent('publish', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    action: 'publish',
    status: publishType || 'live',
  });
};

/**
 * Track property unpublish event
 */
export const trackUnpublishProperty = (propertyId: string, parentAccountId?: string) => {
  analyticsManager.trackPropertyEvent('publish', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    action: 'unpublish',
    status: 'draft',
  });
};

/**
 * Track property list view with filters
 */
export const trackPropertyListFiltered = (parentAccountId?: string, filterCount?: number, totalProperties?: number) => {
  analyticsManager.trackCustomEvent('property_list_filtered', {
    parent_account_id: parentAccountId,
    filter_count: filterCount || 0,
    total_properties: totalProperties || 0,
  });
};

/**
 * Track property image upload
 */
export const trackPropertyImageUpload = (propertyId: string, parentAccountId?: string, imageCount?: number) => {
  analyticsManager.trackCustomEvent('property_image_uploaded', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    image_count: imageCount || 1,
  });
};

/**
 * Track property image deletion
 */
export const trackPropertyImageDelete = (propertyId: string, parentAccountId?: string) => {
  analyticsManager.trackCustomEvent('property_image_deleted', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
  });
};

/**
 * Track property status change
 */
export const trackPropertyStatusChange = (propertyId: string, parentAccountId?: string, newStatus?: string, previousStatus?: string) => {
  analyticsManager.trackCustomEvent('property_status_changed', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    new_status: newStatus,
    previous_status: previousStatus,
  });
};

/**
 * Track property category change
 */
export const trackPropertyCategoryChange = (propertyId: string, parentAccountId?: string, category?: string) => {
  analyticsManager.trackCustomEvent('property_category_changed', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    category: category,
  });
};

/**
 * Track property subscription interaction
 */
export const trackPropertySubscriptionInteraction = (propertyId: string, parentAccountId?: string, action?: string) => {
  analyticsManager.trackCustomEvent('property_subscription_interaction', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    action: action || 'view',
  });
};

/**
 * Track property validation error
 */
export const trackPropertyValidationError = (propertyId: string, parentAccountId?: string, errorCode?: string, errorMessage?: string) => {
  analyticsManager.trackCustomEvent('property_validation_error', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    error_code: errorCode,
    error_message: errorMessage,
  });
};
